import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit';
import { workflowsAPI } from '../../services/api';
import { logout } from './authSlice';

// Everything waiting on the signed-in user: approval / review steps, opinion
// requests (Görüş Al) and contracts an approver sent back for edits.
//
// One list, fetched once and shared. Before this slice the dashboard widget,
// the sidebar badge and the approval notifier each issued their own three
// requests on mount (and again when the user or language loaded), which put
// ~30 calls on the browser's six-connection queue and made the widget's own
// copy land seconds after the page painted.

// A list fetched this recently is reused as-is. Wide enough to collapse every
// mount in a page load into one request, short enough that a route change
// shortly after a decision still refreshes the badge.
export const TASKS_FRESH_MS = 3000;

const EMPTY = [];

const initialState = {
  approvals: EMPTY,
  opinions: EMPTY,
  edits: EMPTY,
  loading: false,
  loaded: false,
  fetchedAt: null,
  error: null,
};

const shape = (data = {}) => ({
  approvals: data.approvals || EMPTY,
  opinions: data.opinions || EMPTY,
  edits: data.contracts || data.edits || EMPTY,
});

export const fetchMyTasks = createAsyncThunk(
  'tasks/fetchMine',
  async () => {
    const { data } = await workflowsAPI.pendingTasks();
    return shape(data);
  },
  {
    condition: (arg, { getState }) => {
      const s = getState().tasks;
      if (s.loading) return false; // one request in flight serves every caller
      if (arg?.force) return true;
      return !s.fetchedAt || Date.now() - s.fetchedAt > TASKS_FRESH_MS;
    },
  }
);

const tasksSlice = createSlice({
  name: 'tasks',
  initialState,
  reducers: {
    // A screen that loaded the list itself (My Tasks) hands it over so the
    // badge and the dashboard widget reflect it without another request.
    tasksReceived: (state, action) => {
      Object.assign(state, shape(action.payload), { loaded: true, fetchedAt: Date.now(), error: null });
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMyTasks.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchMyTasks.fulfilled, (state, action) => {
        Object.assign(state, action.payload, { loading: false, loaded: true, fetchedAt: Date.now() });
      })
      .addCase(fetchMyTasks.rejected, (state, action) => {
        // A skipped dispatch (condition false) is not a failure.
        if (action.meta?.condition) return;
        state.loading = false;
        state.error = action.error?.message || 'Failed to load tasks';
        // Nothing to show yet: let the screens leave the loading state rather
        // than wait forever for a list that is not coming.
        if (!state.loaded) { state.loaded = true; state.fetchedAt = Date.now(); }
      })
      .addCase(logout.fulfilled, () => initialState);
  },
});

export const { tasksReceived } = tasksSlice.actions;

export const selectTasksLoaded = (state) => state.tasks.loaded;

export const selectPendingTaskCount = (state) =>
  state.tasks.approvals.length + state.tasks.opinions.length + state.tasks.edits.length;

// The three kinds merged newest-first, in the row shape the dashboard widget
// renders. Opinion rows and returned contracts wear the shape of an approval
// row: no approval id, no due date, and the moment they landed as assignedAt.
export const selectMergedTasks = createSelector(
  [(s) => s.tasks.approvals, (s) => s.tasks.opinions, (s) => s.tasks.edits],
  (approvals, opinions, edits) => {
    const opinionRows = opinions.map((o) => ({
      _id: `opinion-${o._id}`,
      isOpinionTask: true,
      contractId: o.contractId,
      stepName: o.stepName,
      dueAt: null,
      assignedAt: o.createdAt,
    }));
    const editRows = edits.map((c) => ({
      _id: `edit-${c._id}`,
      isEditTask: true,
      contractId: c,
      stepName: null,
      dueAt: null,
      assignedAt: c.changeRequest?.requestedAt || c.updatedAt,
    }));
    const landedAt = (r) => new Date(r.assignedAt || r.createdAt || 0);
    return [...approvals, ...opinionRows, ...editRows].sort((a, b) => landedAt(b) - landedAt(a));
  }
);

export default tasksSlice.reducer;
